import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, X, Package, CurrencyInr, Receipt,
} from "@phosphor-icons/react";

const KINDS = [
  { value: "product", label: "Product" },
  { value: "waste", label: "Waste type" },
  { value: "service", label: "Service type" },
];
const PATH_BY_KIND = {
  product: "/products",
  waste: "/waste-types",
  service: "/services",
};

function computeGst(price, pct, inclusive) {
  const p = Number(price) || 0, g = Number(pct) || 0;
  if (!p || !g) return 0;
  return inclusive ? +(p - p / (1 + g / 100)).toFixed(2)
                   : +(p * g / 100).toFixed(2);
}

const emptyItem = { kind: "waste", ref_id: "", category: "", name: "", allowed_quantity: 0, excess_rate: 0, gst_percent: 0, gst_inclusive: false };
const emptyPkg = {
  name: "", price: 0, gst_percent: 0, gst_inclusive: true,
  service_category_id: "", service_category_type: "home_service",
  items: [], description: "", status: "active",
  lsgd_ids: [],
};

export default function Packages() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ product: [], waste: [], service: [] });
  const [serviceCats, setServiceCats] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(emptyPkg);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [pkgs, prd, wt, svc, cats, locs] = await Promise.all([
        api.get("/packages"), api.get("/products"),
        api.get("/waste-types"), api.get("/services"),
        api.get("/service-categories"),
        locationsRes(),
      ]);
      setItems(pkgs.data);
      setCatalog({ product: prd.data, waste: wt.data, service: svc.data });
      setServiceCats(cats.data || []);
      setLocations(locs.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const labelOf = (kind, id) => {
    const rows = catalog[kind] || [];
    const row = rows.find((r) => r.id === id);
    if (!row) return "—";
    if (kind === "product") return row.name;
    if (kind === "waste") return `${row.waste_name} (${row.category})`;
    if (kind === "service") return row.name;
    return row.name;
  };

  const reset = () => { setForm(emptyPkg); setEditingId(null); };

  const _newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID() : `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const openNew = () => { reset(); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, price: p.price, gst_percent: p.gst_percent,
      gst_inclusive: p.gst_inclusive,
      items: (p.items || []).map((it) => ({ ...it, _key: _newKey() })),
      service_category_id: p.service_category_id || "",
      service_category_type: p.service_category_type || "home_service",
      description: p.description || "", status: p.status || "active",
      lsgd_ids: p.lsgd_ids || [],
    });
    setOpen(true);
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem, _key: _newKey() }] }));
  const updateItem = (i, patch) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], ...patch };
    if (patch.ref_id !== undefined) {
      items[i].name = labelOf(items[i].kind, patch.ref_id);
    }
    if (patch.category !== undefined) {
      items[i].name = patch.category ? `Category: ${patch.category}` : "";
    }
    if (patch.kind !== undefined) {
      items[i].ref_id = "";
      items[i].category = "";
      items[i].name = "";
    }
    return { ...f, items };
  });
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.service_category_id) { toast.error("Select a Service Category"); return; }
    if ((form.items || []).length === 0) { toast.error("Add at least one item"); return; }
    for (const it of form.items) {
      if (it.kind === "waste") {
        if (!it.category && !it.ref_id) { toast.error("Select category (or specific item) for every waste line"); return; }
      } else if (!it.ref_id) { toast.error("Select item for every line"); return; }
    }
    try {
      const payload = {
        name: form.name,
        service_category_id: form.service_category_id || null,
        service_category_type: form.service_category_type || "home_service",
        price: Number(form.price) || 0,
        gst_percent: Number(form.gst_percent) || 0,
        gst_inclusive: !!form.gst_inclusive,
        items: form.items.map((x) => ({
          kind: x.kind,
          ref_id: x.ref_id || null,
          category: x.kind === "waste" ? (x.category || null) : null,
          name: x.name,
          allowed_quantity: Number(x.allowed_quantity) || 0,
          excess_rate: Number(x.excess_rate) || 0,
          gst_percent: Number(x.gst_percent) || 0,
          gst_inclusive: !!x.gst_inclusive,
        })),
        description: form.description || "",
        status: form.status || "active",
        lsgd_ids: form.lsgd_ids || [],
      };
      if (editingId) {
        await api.put(`/packages/${editingId}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/packages", payload);
        toast.success("Package created");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try { await api.delete(`/packages/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const gstAmt = useMemo(() => computeGst(form.price, form.gst_percent, form.gst_inclusive), [form.price, form.gst_percent, form.gst_inclusive]);
  const totalWithGst = form.gst_inclusive
    ? Number(form.price || 0)
    : Number(form.price || 0) + gstAmt;

  return (
    <AppShell title="Packages" subtitle="Service & Pricing"
      actions={
        <button data-testid="btn-new-package" onClick={openNew}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold" />New package
        </button>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9]">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">All packages</div>
          <div className="font-heading text-lg">Bundled product / waste / service offers</div>
        </div>
        <table className="w-full text-sm" data-testid="packages-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Price</th>
              <th className="px-6 py-3">GST</th>
              <th className="px-6 py-3">Items</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-[#8A8780]">No packages yet.</td></tr>
            )}
            {items.map((p, idx) => (
              <tr key={p.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`pkg-row-${p.id}`}>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Package size={14} color="#2D5A27" weight="fill" />
                    <span className="font-medium">{p.name}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded ${
                      p.service_category_type === "drop"
                        ? "bg-[#D6E4F5] text-[#1F4E8C]"
                        : "bg-[#D4E0D1] text-[#1A3317]"
                    }`}>{p.service_category_type === "drop" ? "Drop" : "On-Premises"}</span>
                  </div>
                  {p.description && <div className="text-xs text-[#8A8780] mt-0.5">{p.description}</div>}
                </td>
                <td className="px-6 py-3 font-mono">₹{Number(p.price).toFixed(2)}</td>
                <td className="px-6 py-3 text-xs">
                  {p.gst_percent > 0
                    ? <>{Number(p.gst_percent).toFixed(1)}% · {p.gst_inclusive ? "incl." : "excl."}</>
                    : <span className="text-[#8A8780]">—</span>}
                </td>
                <td className="px-6 py-3 text-xs text-[#5C5A55]">
                  {(p.items || []).slice(0, 3).map((it, i) => (
                    <div key={i}>
                      • {it.kind === "waste" && it.category
                          ? `${it.category} category`
                          : (it.name || labelOf(it.kind, it.ref_id))} — up to {it.allowed_quantity}{it.kind === "waste" ? "kg" : ""}
                      {it.excess_rate > 0 && <span className="text-[#8A8780]"> · +₹{it.excess_rate}/{it.kind === "waste" ? "kg" : "unit"} over</span>}
                    </div>
                  ))}
                  {(p.items || []).length > 3 && <div className="text-[#8A8780]">+{p.items.length - 3} more</div>}
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${p.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#E5E2D9] text-[#5C5A55]"}`}>{p.status}</span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button data-testid={`pkg-edit-${p.id}`} onClick={() => openEdit(p)} className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-4"><PencilSimple size={12} />Edit</button>
                  <button data-testid={`pkg-del-${p.id}`} onClick={() => del(p.id)} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12} />Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#E5E2D9] rounded-md w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Service & Pricing</div>
                <div className="font-heading text-lg">{editingId ? "Edit" : "New"} package</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16} /></button>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="package-form">
              {/* Step 1 — Service Category & Type (mandatory) */}
              <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3 space-y-3" data-testid="pkg-svc-block">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Step 1 · Service Category</div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1.5">Service Category *</label>
                  <select required value={form.service_category_id || ""}
                          onChange={(e) => {
                            const cat = serviceCats.find(c => c.id === e.target.value);
                            setForm(f => ({
                              ...f,
                              service_category_id: e.target.value,
                              // Auto-derive type from selected category (still overrideable below).
                              service_category_type: cat?.kind || f.service_category_type,
                            }));
                          }}
                          data-testid="pkg-svc-cat"
                          className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                    <option value="">— select category —</option>
                    {serviceCats.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.kind === "drop" ? "(Drop)" : "(Home Service)"}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1.5">Fulfilment Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "home_service", l: "On-Premises", d: "Team visits the customer's address" },
                      { v: "drop", l: "Drop", d: "Customer drops off at our center" },
                    ].map(opt => (
                      <button key={opt.v} type="button"
                              onClick={() => setForm(f => ({ ...f, service_category_type: opt.v }))}
                              data-testid={`pkg-type-${opt.v}`}
                              className={`text-left p-2.5 rounded-md border transition ${
                                form.service_category_type === opt.v
                                  ? "border-[#2D5A27] bg-[#EFF3EE]"
                                  : "border-[#E5E2D9] bg-white hover:bg-[#F7F6F2]"
                              }`}>
                        <div className="text-sm font-medium">{opt.l}</div>
                        <div className="text-[10px] text-[#5C5A55]">{opt.d}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Package name *</label>
                  <input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                         data-testid="pkg-name" placeholder="e.g. RED Prime"
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Price ₹ *</label>
                  <div className="relative">
                    <CurrencyInr size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
                    <input required type="number" step="any" min="0" value={form.price}
                           onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                           data-testid="pkg-price"
                           className="w-full h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md font-mono" />
                  </div>
                </div>
              </div>

              {/* LSGD scoping — select which LSGDs this package is available in */}
              <LsgdScopePicker
                locations={locations}
                selected={form.lsgd_ids}
                onChange={(next) => setForm(f => ({ ...f, lsgd_ids: next }))}
              />

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">GST %</label>
                  <input type="number" step="0.1" min="0" value={form.gst_percent}
                         onChange={(e) => setForm(f => ({ ...f, gst_percent: e.target.value }))}
                         data-testid="pkg-gst-pct"
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">GST treatment</label>
                  <select value={form.gst_inclusive ? "incl" : "excl"}
                          onChange={(e) => setForm(f => ({ ...f, gst_inclusive: e.target.value === "incl" }))}
                          data-testid="pkg-gst-mode"
                          className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                    <option value="incl">Inclusive (price contains GST)</option>
                    <option value="excl">Exclusive (GST added on top)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Status</label>
                  <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                          className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {form.gst_percent > 0 && form.price > 0 && (
                <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3 text-xs font-mono text-[#5C5A55] flex items-center gap-2" data-testid="pkg-gst-preview">
                  <Receipt size={14} color="#2D5A27" />
                  GST amount: ₹{gstAmt.toFixed(2)} · Total billed: ₹{totalWithGst.toFixed(2)}
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Description (optional)</label>
                <input value={form.description}
                       onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
              </div>

              <div className="border-t border-[#E5E2D9] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Items included</div>
                  <button type="button" onClick={addItem} data-testid="pkg-add-item"
                          className="h-8 px-2.5 text-xs border border-[#E5E2D9] rounded inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                    <Plus size={11} weight="bold" />Add item
                  </button>
                </div>
                {form.items.length === 0 && (
                  <div className="text-xs text-[#8A8780] bg-[#F7F6F2] border border-[#E5E2D9] border-dashed rounded p-3 text-center">
                    No items. Click "Add item" to bundle products, waste types or services.
                  </div>
                )}
                <div className="space-y-2" data-testid="pkg-items">
                  {form.items.map((it, i) => (
                    <div key={it._key || i}
                         className="bg-[#FAFAF7] border border-[#E5E2D9] rounded-md p-2.5 space-y-2"
                         data-testid={`pkg-item-${i}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">Kind</label>
                          <select value={it.kind} onChange={(e) => updateItem(i, { kind: e.target.value })}
                                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </select>
                        </div>
                        <div className="col-span-4">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">
                            {it.kind === "waste" ? "Category" : "Item"}
                          </label>
                          {it.kind === "waste" ? (
                            <select value={it.category || ""} onChange={(e) => updateItem(i, { category: e.target.value })}
                                    data-testid={`pkg-item-cat-${i}`}
                                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                              <option value="">— select category —</option>
                              {Array.from(new Set((catalog.waste || []).map(r => r.category))).filter(Boolean).sort().map(cat => {
                                const count = (catalog.waste || []).filter(r => r.category === cat).length;
                                return <option key={cat} value={cat}>{cat} · {count} waste type{count === 1 ? "" : "s"}</option>;
                              })}
                            </select>
                          ) : (
                            <select value={it.ref_id} onChange={(e) => updateItem(i, { ref_id: e.target.value })}
                                    data-testid={`pkg-item-ref-${i}`}
                                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                              <option value="">— select —</option>
                              {(catalog[it.kind] || []).map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">
                            Allowed {it.kind === "waste" ? "kg" : "qty"}
                          </label>
                          <input type="number" step="any" min="0" value={it.allowed_quantity}
                                 onChange={(e) => updateItem(i, { allowed_quantity: e.target.value })}
                                 data-testid={`pkg-item-qty-${i}`}
                                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm font-mono" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">Extra/unit</label>
                          <input type="number" step="any" min="0" value={it.excess_rate}
                                 onChange={(e) => updateItem(i, { excess_rate: e.target.value })}
                                 data-testid={`pkg-item-excess-${i}`}
                                 placeholder="₹"
                                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm font-mono" />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <button type="button" onClick={() => removeItem(i)}
                                  data-testid={`pkg-item-del-${i}`}
                                  className="h-9 px-2 text-[#C25D38] hover:bg-[#C25D38]/8 rounded">
                            <Trash size={12} />
                          </button>
                        </div>
                      </div>
                      {/* Per-item GST config — used to tax the "Extra/unit" line */}
                      <div className="grid grid-cols-12 gap-2 items-end pt-1 border-t border-dashed border-[#E5E2D9]">
                        <div className="col-span-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#8A8780]">
                          <Receipt size={12} color="#2D5A27" />
                          Extra GST
                        </div>
                        <div className="col-span-3">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">GST %</label>
                          <input type="number" step="0.1" min="0" value={it.gst_percent ?? 0}
                                 onChange={(e) => updateItem(i, { gst_percent: e.target.value })}
                                 data-testid={`pkg-item-gst-pct-${i}`}
                                 placeholder="0"
                                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm font-mono" />
                        </div>
                        <div className="col-span-5">
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] block mb-1">GST treatment</label>
                          <select value={it.gst_inclusive ? "incl" : "excl"}
                                  onChange={(e) => updateItem(i, { gst_inclusive: e.target.value === "incl" })}
                                  data-testid={`pkg-item-gst-mode-${i}`}
                                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                            <option value="excl">Exclusive (add on top)</option>
                            <option value="incl">Inclusive (built-in)</option>
                          </select>
                        </div>
                        <div className="col-span-1" />
                      </div>
                      {Number(it.excess_rate) > 0 && Number(it.gst_percent) > 0 && (
                        <div className="text-[10px] font-mono text-[#5C5A55] pl-1">
                          Extra unit price: ₹{Number(it.excess_rate).toFixed(2)}
                          {it.gst_inclusive
                            ? <> · GST/unit ₹{(Number(it.excess_rate) - Number(it.excess_rate) * 100 / (100 + Number(it.gst_percent))).toFixed(2)} (embedded)</>
                            : <> · GST/unit ₹{(Number(it.excess_rate) * Number(it.gst_percent) / 100).toFixed(2)} (on top → ₹{(Number(it.excess_rate) * (1 + Number(it.gst_percent) / 100)).toFixed(2)})</>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {form.items.length > 0 && (
                  <div className="text-[11px] text-[#8A8780] mt-2">
                    Tip: "Extra/unit" is billed only when collected quantity exceeds the allowed amount for the period.
                  </div>
                )}
              </div>

              <button type="submit" data-testid="pkg-save"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
                {editingId ? "Save changes" : "Create package"}
              </button>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}


// ─────────────────────────────────────────────────────────────
// LSGD-scope picker (state → district → LSGD) for package availability
// ─────────────────────────────────────────────────────────────
function LsgdScopePicker({ locations, selected, onChange }) {
  const [stateId, setStateId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const states = useMemo(() => locations.filter(l => l.type === "state"), [locations]);
  const districts = useMemo(
    () => locations.filter(l => l.type === "district" && (!stateId || l.parent_id === stateId)),
    [locations, stateId],
  );
  const lsgds = useMemo(
    () => locations.filter(l => l.type === "lsgd" && (!districtId || l.parent_id === districtId)),
    [locations, districtId],
  );
  const lsgdById = useMemo(() => {
    const m = {};
    for (const l of locations) if (l.type === "lsgd") m[l.id] = l;
    return m;
  }, [locations]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  const selectAll = () => {
    const ids = lsgds.map(l => l.id);
    const next = new Set(selected);
    ids.forEach(id => next.add(id));
    onChange(Array.from(next));
  };
  const clearAll = () => onChange([]);

  return (
    <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3 space-y-3"
         data-testid="pkg-lsgd-scope">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">LSGD scope · optional</div>
          <div className="text-[10px] text-[#8A8780] mt-0.5">
            {selected.length === 0
              ? "Leave empty to make this package available everywhere."
              : `Restricted to ${selected.length} LSGD${selected.length === 1 ? "" : "s"} — only visible to customers in those LSGDs.`}
          </div>
        </div>
        {selected.length > 0 && (
          <button type="button" onClick={clearAll}
                  data-testid="pkg-lsgd-clear"
                  className="text-[10px] uppercase tracking-[0.18em] text-[#C25D38] hover:underline">
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1">State</label>
          <select value={stateId} onChange={e => { setStateId(e.target.value); setDistrictId(""); }}
                  data-testid="pkg-lsgd-state"
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs">
            <option value="">— select state —</option>
            {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] block mb-1">District</label>
          <select value={districtId} onChange={e => setDistrictId(e.target.value)}
                  disabled={!stateId}
                  data-testid="pkg-lsgd-district"
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md bg-white text-xs disabled:opacity-60">
            <option value="">— select district —</option>
            {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      {stateId && districtId && (
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">
              LSGDs ({lsgds.length})
            </label>
            {lsgds.length > 0 && (
              <button type="button" onClick={selectAll}
                      data-testid="pkg-lsgd-all"
                      className="text-[10px] uppercase tracking-[0.18em] text-[#2D5A27] hover:underline">
                Select all
              </button>
            )}
          </div>
          <div className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md bg-white p-2"
               data-testid="pkg-lsgd-list">
            {lsgds.length === 0 && (
              <div className="text-[11px] text-[#8A8780] text-center py-2">No LSGDs in this district.</div>
            )}
            {lsgds.map(l => (
              <label key={l.id}
                     className="flex items-center gap-2 py-1 px-1 rounded hover:bg-[#F7F6F2] cursor-pointer">
                <input type="checkbox"
                       checked={selected.includes(l.id)}
                       onChange={() => toggle(l.id)}
                       data-testid={`pkg-lsgd-check-${l.id}`}
                       className="accent-[#2D5A27]"/>
                <span className="text-xs flex-1">{l.name}</span>
                {l.lsgd_type && (
                  <span className="text-[9px] uppercase tracking-[0.15em] text-[#8A8780]">
                    {l.lsgd_type.replace(/_/g, " ")}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="border-t border-[#E5E2D9] pt-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mb-1">Selected LSGDs</div>
          <div className="flex flex-wrap gap-1">
            {selected.map(id => (
              <span key={id}
                    className="inline-flex items-center gap-1 text-[10px] font-mono bg-[#EFF3EE] text-[#1A3317] px-2 py-0.5 rounded">
                {lsgdById[id]?.name || id}
                <button type="button" onClick={() => onChange(selected.filter(x => x !== id))}
                        className="hover:text-[#C25D38]">×</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
